import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUserStore } from "@/store/userStore";
import {
  createExpense,
  createSupplier,
  deleteExpense,
  deleteSupplier,
  generateRecurring,
  getCashflow,
  getUpcoming,
  listExpenses,
  listSuppliers,
  markExpensePaid,
  updateExpense,
  updateSupplier,
  type Expense,
  type ExpenseFiltersParams,
  type ExpenseMethod,
  type Supplier,
} from "@/services/edgeFunctionWrappers/accountingManagementWrapper";

// Petit contrat commun : les erreurs des wrappers sont levées pour que
// react-query les expose (les composants toast-ent sur onError).
const unwrap = <T,>({ data, error }: { data: T | null; error: unknown }): T => {
  if (error) throw error;
  return data as T;
};

export function useCashflow(period: { start_date: string; end_date: string }, months = 12) {
  const gymId = useUserStore((s) => s.gymId);
  return useQuery({
    queryKey: ["accounting-cashflow", gymId, period.start_date, period.end_date, months],
    queryFn: async () => unwrap(await getCashflow({ gym_id: gymId!, ...period, months })),
    enabled: !!gymId,
    staleTime: 30_000,
  });
}

export function useExpenses(filters: Omit<ExpenseFiltersParams, "gym_id">) {
  const gymId = useUserStore((s) => s.gymId);
  return useQuery({
    queryKey: ["accounting-expenses", gymId, filters],
    queryFn: async () => unwrap(await listExpenses({ gym_id: gymId!, ...filters })),
    enabled: !!gymId,
  });
}

export function useSuppliers(includeInactive = false) {
  const gymId = useUserStore((s) => s.gymId);
  return useQuery({
    queryKey: ["accounting-suppliers", gymId, includeInactive],
    queryFn: async () => unwrap(await listSuppliers(gymId!, includeInactive)),
    enabled: !!gymId,
  });
}

export function useUpcoming(period: { start_date: string; end_date: string }) {
  const gymId = useUserStore((s) => s.gymId);
  return useQuery({
    queryKey: ["accounting-upcoming", gymId, period.start_date, period.end_date],
    queryFn: async () => unwrap(await getUpcoming(gymId!, period)),
    enabled: !!gymId,
  });
}

export function useAccountingMutations() {
  const gymId = useUserStore((s) => s.gymId);
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["accounting-expenses"] });
    qc.invalidateQueries({ queryKey: ["accounting-cashflow"] });
    qc.invalidateQueries({ queryKey: ["accounting-upcoming"] });
  };
  const invalidateSuppliers = () =>
    qc.invalidateQueries({ queryKey: ["accounting-suppliers"] });
  // Le nom du fournisseur est joint dans le journal : renommer ou
  // désactiver un fournisseur doit rafraîchir aussi les dépenses.
  const invalidateSuppliersAndExpenses = () => {
    invalidateSuppliers();
    qc.invalidateQueries({ queryKey: ["accounting-expenses"] });
  };

  return {
    createExpense: useMutation({
      mutationFn: async (params: Omit<Parameters<typeof createExpense>[0], "gym_id">) =>
        unwrap(await createExpense({ gym_id: gymId!, ...params })),
      onSuccess: invalidate,
    }),
    updateExpense: useMutation({
      mutationFn: async ({ id, patch }: { id: string; patch: Partial<Expense> }) =>
        unwrap(await updateExpense(id, gymId!, patch)),
      onSuccess: invalidate,
    }),
    deleteExpense: useMutation({
      mutationFn: async (id: string) => unwrap(await deleteExpense(id, gymId!)),
      onSuccess: invalidate,
    }),
    markPaid: useMutation({
      mutationFn: async ({ id, payment_method }: { id: string; payment_method?: ExpenseMethod }) =>
        unwrap(await markExpensePaid(id, gymId!, { payment_method })),
      onSuccess: invalidate,
    }),
    createSupplier: useMutation({
      mutationFn: async (supplier: Parameters<typeof createSupplier>[1]) =>
        unwrap(await createSupplier(gymId!, supplier)),
      onSuccess: invalidateSuppliers,
    }),
    updateSupplier: useMutation({
      mutationFn: async ({ id, patch }: { id: string; patch: Partial<Supplier> }) =>
        unwrap(await updateSupplier(id, gymId!, patch)),
      onSuccess: invalidateSuppliersAndExpenses,
    }),
    deleteSupplier: useMutation({
      mutationFn: async (id: string) => unwrap(await deleteSupplier(id, gymId!)),
      onSuccess: invalidateSuppliersAndExpenses,
    }),
    generateRecurring: useMutation({
      mutationFn: async (month?: string) => unwrap(await generateRecurring(gymId!, month)),
      onSuccess: invalidate,
    }),
  };
}
